// ParticleBurst.jsx
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { EMPTY_ARRAY } from '../resource/defaults.js';
import { color } from '../style/variables.js';

// The spec's delays are 250ms later than these: that lead-in is the idle head
// of a looping Figma preview, and on a real tap it reads as a dropped press.
const PARTICLE_DURATION = 600;
const PARTICLE_DELAY_MIN = 130;
const PARTICLE_DELAY_MAX = 230;
const PARTICLE_COUNT = 10;
const PARTICLE_VIEW_BOX_SIZE = 16;

const ParticleShape = {
  DOT: 'dot',
  RING: 'ring',
  SPIRAL: 'spiral',
  WAVE: 'wave',
  CURVE: 'curve',
};

// Drawn as SVG rather than CSS borders: a border can only cut a flat-ended
// slice out of a circle, which reads as a broken ring.
const PARTICLE_PATHS = {
  [ParticleShape.SPIRAL]: 'M2.5 10.5C2.5 6.5 6.5 6.5 6.5 10s4 3.5 4-2',
  [ParticleShape.WAVE]: 'M2 9C4 5 7 12 11 7',
  [ParticleShape.CURVE]: 'M3.5 12.5C3.5 6 7.5 3 12.5 3.5',
};

const PARTICLE_SHAPES = [
  ParticleShape.DOT,
  ParticleShape.DOT,
  ParticleShape.DOT,
  ParticleShape.RING,
  ParticleShape.SPIRAL,
  ParticleShape.WAVE,
  ParticleShape.CURVE,
];

const PARTICLE_COLORS = [
  color.lovense,
  color.green,
  color.sky,
  color.softBlue,
  color.purple,
  color.tealBlue,
];

// Sizes and travel are in em so the burst rides the button's own responsive
// font size instead of needing a second set of breakpoints.
const PARTICLE_SIZE_RANGE = {
  [ParticleShape.DOT]: { min: 0.4, max: 0.6 },
  [ParticleShape.RING]: { min: 0.5, max: 0.7 },
  [ParticleShape.SPIRAL]: { min: 0.7, max: 0.95 },
  [ParticleShape.WAVE]: { min: 0.7, max: 0.95 },
  [ParticleShape.CURVE]: { min: 0.65, max: 0.9 },
};
const PARTICLE_TRAVEL_RANGE = { min: 0.5, max: 1 };

export const BURST_DURATION = PARTICLE_DELAY_MAX + PARTICLE_DURATION;

const randomBetween = ({ min, max }) => min + Math.random() * (max - min);

const randomFrom = ({ items }) =>
  items[Math.floor(Math.random() * items.length)];

/**
 * Build one randomized burst.
 *
 * @returns {object[]} Particles, each carrying its shape and the inline style
 * driving its travel and pop animations.
 */
const createParticles = () =>
  Array.from({ length: PARTICLE_COUNT }, (unusedValue, index) => {
    // Evenly spaced base angle plus jitter, so the scatter never clumps.
    const angle =
      (360 / PARTICLE_COUNT) * index + randomBetween({ min: -18, max: 18 });
    const radian = (angle * Math.PI) / 180;
    // A percentage radius makes the spread an ellipse that hugs any button.
    const radius = randomBetween({ min: 70, max: 105 });
    const distance = randomBetween(PARTICLE_TRAVEL_RANGE);
    const shape = randomFrom({ items: PARTICLE_SHAPES });
    const rotate = Math.round(randomBetween({ min: 0, max: 360 }));
    const size = randomBetween(PARTICLE_SIZE_RANGE[shape]);
    const particleColor = randomFrom({ items: PARTICLE_COLORS });
    const isDot = ParticleShape.DOT === shape;
    const delay = `${Math.round(
      randomBetween({ min: PARTICLE_DELAY_MIN, max: PARTICLE_DELAY_MAX })
    )}ms`;
    return {
      id: `${index}-${shape}-${rotate}`,
      shape,
      travelStyle: {
        left: `${50 + radius * Math.cos(radian)}%`,
        top: `${50 + radius * Math.sin(radian)}%`,
        animationDelay: delay,
        '--particle-move-x': `${distance * Math.cos(radian)}em`,
        '--particle-move-y': `${distance * Math.sin(radian)}em`,
      },
      popStyle: {
        width: `${size}em`,
        height: `${size}em`,
        animationDelay: delay,
        '--particle-rotate': `${rotate}deg`,
        '--particle-fill': isDot ? particleColor : 'none',
        '--particle-stroke': isDot ? 'none' : particleColor,
      },
    };
  });

// The dot nearly fills its view box so its size reads as the actual dot
// diameter; the ring leaves room for the stroke it paints outward.
const renderParticleShape = ({ shape }) => {
  if (ParticleShape.DOT === shape) {
    return <circle cx="8" cy="8" r="7" />;
  }
  if (ParticleShape.RING === shape) {
    return <circle cx="8" cy="8" r="5" />;
  }
  return <path d={PARTICLE_PATHS[shape]} />;
};

export const ParticleBurst = ({ isActive = false, anchorRef = null }) => {
  const particles = useMemo(
    () => (isActive ? createParticles() : EMPTY_ARRAY),
    [isActive]
  );
  // Particles fly past the button and its containers clip for their own
  // reasons, so the burst is measured off the anchor and portaled to the body.
  // Measured on the render that switches it on, before the anchor's own
  // animation applies, so the box is its resting one.
  const anchorStyle = useMemo(() => {
    const anchor = anchorRef?.current;
    if (!isActive || !anchor) {
      return null;
    }
    const { left, top, width, height } = anchor.getBoundingClientRect();
    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      fontSize: window.getComputedStyle(anchor).fontSize,
    };
  }, [isActive, anchorRef]);
  if (!isActive || !anchorStyle) {
    return null;
  }
  return createPortal(
    <StyledParticleBurst aria-hidden style={anchorStyle}>
      {particles.map(({ id, shape, travelStyle, popStyle }) => (
        <ParticleTravel key={id} style={travelStyle}>
          <Particle
            viewBox={`0 0 ${PARTICLE_VIEW_BOX_SIZE} ${PARTICLE_VIEW_BOX_SIZE}`}
            style={popStyle}
          >
            {renderParticleShape({ shape })}
          </Particle>
        </ParticleTravel>
      ))}
    </StyledParticleBurst>,
    document.body
  );
};

ParticleBurst.propTypes = {
  isActive: PropTypes.bool,
  anchorRef: PropTypes.object,
};

// The burst is inert and gone within a second, so its z-index only has to
// clear the anchor's own page, not modals.
const StyledParticleBurst = styled.span`
  position: fixed;
  z-index: 1;
  pointer-events: none;
`;

// Both keyframes read the per-particle values as CSS custom properties instead
// of styled-components interpolation, which would inject a fresh class and
// @keyframes rule per particle per burst and never release them.
//
// Travel and pop are split across two elements because easing applies between
// every pair of keyframes: sharing one transform would force the drift through
// the scale's keyframes, stopping and re-accelerating at each one.
const TravelKeyframes = keyframes`
  from { transform: translate(0, 0); }
  to {
    transform: translate(var(--particle-move-x), var(--particle-move-y));
  }
`;

// The spec's spring is baked into the scale values, each leg carrying its own
// easing. Scale is undefined at 80% so 50%-100% reads as one fade-out.
const PopKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: scale(0) rotate(var(--particle-rotate));
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }
  13% {
    opacity: 1;
  }
  20% {
    transform: scale(1.15) rotate(var(--particle-rotate));
    animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
  }
  50% {
    transform: scale(1) rotate(var(--particle-rotate));
    animation-timing-function: cubic-bezier(0.4, 0, 1, 1);
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(0.5) rotate(var(--particle-rotate));
  }
`;

const ParticleTravel = styled.span`
  position: absolute;
  will-change: transform;
  animation: ${TravelKeyframes} ${PARTICLE_DURATION}ms
    cubic-bezier(0, 0, 0.58, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Particle = styled.svg`
  display: block;
  overflow: visible;
  opacity: 0;
  will-change: transform, opacity;
  fill: var(--particle-fill);
  stroke: var(--particle-stroke);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  animation: ${PopKeyframes} ${PARTICLE_DURATION}ms linear forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default ParticleBurst;
